import get from 'lodash/get';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DoorIcon from '@mui/icons-material/DoorFront';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import AppointmentListItem from '../scheduling/AppointmentListItem';
import BlankContent from '../common/BlankContent';
import MetricWidget from '../common/MetricWidget';
import SelectPatientForm from '../scheduling/SelectPatientForm';
import MetricsOverview from './MetricsOverview';
import CustomBox from '../common/CustomBox';

import {
  DescriptionBlueIcon,
  BluePersonIcon,
  EmailIcon,
  ScheduleWhiteIcon,
  CalendarWhiteIcon,
  SearchBlueIcon,
  ArrowRightBlueIcon
} from '../../styles/icons';
import useIsMobile from '../../hooks/useIsMobile';
import { MAX_WITH_MOBILE_MEDIA } from '../../config/getTheme';
import './ProviderHomeScreen.scss';

const SearchPatients = props => {
  return (
    <CustomBox
      className='phs-search'
      sx={{
        width: '100%',
        cursor: 'pointer',
        padding: '12px 8px'
      }}
      onClick={props.onClick}
    >
      <Stack direction='row' spacing={1} alignItems='center'>
        <SearchBlueIcon style={{width: '15px', height: '15px'}} />
        <Typography variant='large500Tight'>
          Search patients
        </Typography>
      </Stack>
    </CustomBox>
  );
};

const ProviderHomeScreen = props => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [scheduleDialogIsOpen, setScheduleDialogIsOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const { provider, appointments } = props;
  const summaryStats = [
    {
      title: 'Sessions',
      Icon: DescriptionBlueIcon,
      count: get(props, 'sessionCount', 0) || 0,
      countLabel: 'You have {count} incomplete notes that need to be finalized',
      onClick: () => navigate('/providers/sessions?is_completed=false'),
    },
    {
      title: 'Patients',
      Icon: BluePersonIcon,
      count: get(props, 'patientCount', 0) || 0,
      countLabel: 'You have {count} patients without a scheduled appointment',
      onClick: () => navigate('/providers/patients?patient_status=Unscheduled'),
    },
    {
      title: 'Messages',
      Icon: EmailIcon,
      count: get(props, 'messageCount', 0) || 0,
      countLabel: 'You have {count} unread messages in your inbox ',
      onClick: () => navigate('/providers/messages'),
    },
    {
      title: 'Leads',
      Icon: DoorIcon,
      count: get(props, 'metricsLeadCount', 0) || 0,
      countLabel: 'You have {count} leads with updates.',
      onClick: () => navigate('/providers/leads/list?has_updates=true'),
    },
  ];

  const toggleAccordion = () => {
    if (isMobile) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <Box className='ProviderHomeScreen'>
      <Box>
          {isMobile &&
            <Box sx={{ marginTop: '8px', marginBottom: '18px' }}>
              <Stack
                direction='row'
                alignItems='center'
                className='ProviderHome__head__mobile-search'
                onClick={() => setScheduleDialogIsOpen(true)}
              >
                <SearchBlueIcon />
                <Typography variant='normal500' color='text.primary'>
                  Search Patients
                </Typography>
              </Stack>
            </Box>
          }
          <Grid container>
            {
              summaryStats.map((s, i) =>
                <Grid item xs={6} sm={3} md={2} key={'summaryStat-' + i} padding={.5}>
                  <MetricWidget key={'MetricWidget-' + i} {...s} />
                </Grid>
              )
            }
            {!isMobile &&
              <Grid item xs={12} sm={6} md={4} lg={4} xl={3} padding={.5}>
                <MetricsOverview {...props} />
              </Grid>
            }
          </Grid>
          {!isMobile 
            ? <Stack direction='row' justifyContent='space-between' alignItems='center' marginTop='2em' marginBottom='1em'>
                <Box sx={{maxWidth: '400px'}}>
                  <SearchPatients
                    onClick={() => setScheduleDialogIsOpen(true)} 
                  />
                </Box>
                <Button
                  variant='secondaryGradient'
                  size='large'
                  nowrap
                  onClick={() => setScheduleDialogIsOpen(true)}
                  endIcon={<ScheduleWhiteIcon />}
                >
                  Schedule Session
                </Button>
            </Stack>
            : null
          }
            
        <Stack>
            <Dialog
              fullWidth={true}
              maxWidth='sm'
              open={scheduleDialogIsOpen}
              onClose={() => setScheduleDialogIsOpen(false)}
            >
              <SelectPatientForm />
            </Dialog>
          <CustomBox className='phs-sessions'>
            <Box className='phs-sessions__inner'>
              <Stack
                className='phs-sessions__head'
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  className='phs-sessions__head__title-group'
                  onClick={toggleAccordion}
                >
                  <Stack direction='row' alignItems='center' className='phs-sessions__head__title-group__icon-text'>
                    <Box className='phs-sessions__head__title-group__icon-text__icon'>
                      <CalendarWhiteIcon style={{width: '1.125rem', height: '1.125rem'}} />
                    </Box>
                    <Typography
                      variant='h2'
                      color='text.primary'
                      sx={{
                        lineHeight: '1.875rem',
                        marginLeft: '12px',
                        [MAX_WITH_MOBILE_MEDIA]: {
                          fontSize: '1.125rem',
                        },
                      }}
                    >
                      Today's Sessions
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' style={{ display: !isMobile ? 'none' : '' }}>
                    <Typography
                      variant='large600Tight'
                      color='text.orange'
                      sx={{
                        lineHeight: '1.875rem',
                        marginRight: '8px',
                      }}
                    >
                      ({appointments ? appointments.length : 0})
                    </Typography>
                    <Button sx={{ padding: "0", minWidth: "auto" }}
                      onClick={() => navigate('/providers/sessions?is_completed=false')}>
                      <ArrowRightBlueIcon />
                    </Button>
                  </Stack>
                </Stack>

                <Stack direction='row' alignItems='center' justifyContent='space-between' className='phs-sessions__head__schedule-group'>
                  <Typography
                    variant='h2Inter'
                    color='text.blue'
                    sx={{
                      lineHeight: '1rem',
                      [MAX_WITH_MOBILE_MEDIA]: {
                        fontSize: '0.875rem',
                      },
                    }}
                  >
                    {moment().format('MM/DD/YYYY')}
                  </Typography>

                  <Box className='phs-sessions__head__schedule-group__buttons' style={{ display: !isMobile ? 'none' : '' }}>
                    <Button
                      variant='secondaryGradient'
                      size='small'
                      onClick={() => setScheduleDialogIsOpen(true)}
                      endIcon={<ScheduleWhiteIcon />}
                    >
                      Schedule Session
                    </Button>
                  </Box>
                </Stack>
              </Stack>

              <Box padding='0 10px' className={`mx-table-responsive ${isOpen ? 'open' : ''}`}>
                <Box className='phs-sessions__appointment-list mx-table'>
                  {(appointments || [])
                    .filter(a => a.is_canceled !== true)
                    .map(a =>
                      <AppointmentListItem key={a.id} appointment={a} isMobile={isMobile} />
                    )
                  }
                  {!appointments || !appointments.length
                    ? <BlankContent
                      title='No appointments found'
                      body="It looks like you don't have any appointments today"
                      Icon={EventBusyIcon}
                    />
                    : null
                  }
                </Box>
              </Box>
            </Box>
          </CustomBox>
        </Stack>

        {isMobile && <MetricsOverview {...props} />}
      </Box>
    </Box>
  );
};

ProviderHomeScreen.propTypes = {
  provider: PropTypes.object,
  appointments: PropTypes.arrayOf(PropTypes.object),
  sessionCount: PropTypes.number,
  messageCount: PropTypes.number,
  patientCount: PropTypes.number,
  metricsSessionCount: PropTypes.number,
  metricsSessionPriorPeriodCount: PropTypes.number,
  metricsSessionGoal: PropTypes.number,
  refetch: PropTypes.func,
}

export default ProviderHomeScreen;
